import { IconButton, ListItem, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, ExternalLink, FileText, MessageCircle, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  errorMessage,
  fetchApiFile,
  listSpaceDocumentsOptions,
  listSpaceDocumentsQueryKey,
  listSpaceSharedChatsOptions,
  removeSpaceDocumentMutation,
  type SpaceDocumentResponse,
} from "@/api";
import { QuerySection } from "@/components/section";
import { formatRelativeTime } from "@/i18n/format";
import { saveBlob } from "@/lib/download";
import { SpaceSection } from "./SpaceSection";

/** Opened as plain text so shared HTML or Markdown is shown, never run. */
const TEXT_TYPES = /^(text\/|application\/(json|xml))/;

/**
 * What people shared with everyone in the space: whole chats (read-only, forkable) and
 * documents (snapshots members open without the source chat).
 */
export function SharedSection({ spaceUuid }: Readonly<{ spaceUuid: string }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "detail.shared" });
  const toast = useToast();
  const queryClient = useQueryClient();
  const path = { space_uuid: spaceUuid };
  const navigate = useNavigate();
  const docs = useQuery({ ...listSpaceDocumentsOptions({ path }), select: (r) => r.data });
  const chats = useQuery({ ...listSpaceSharedChatsOptions({ path }), select: (r) => r.data });
  const both = {
    data: docs.data && chats.data ? { docs: docs.data, chats: chats.data } : undefined,
    isPending: docs.isPending || chats.isPending,
    isError: docs.isError || chats.isError,
    refetch: () => Promise.all([docs.refetch(), chats.refetch()]),
  };
  const remove = useMutation({
    ...removeSpaceDocumentMutation(),
    onSuccess: () => toast.success(t("removed")),
    onError: (error) => toast.error(errorMessage(error, t("removeFailed"))),
    onSettled: () => queryClient.invalidateQueries({ queryKey: listSpaceDocumentsQueryKey({ path }) }),
  });

  const download = async (doc: SpaceDocumentResponse) => {
    try {
      const file = await fetchApiFile(doc.url);
      saveBlob(file.blob, file.filename ?? doc.filename);
    } catch (error) {
      toast.error(errorMessage(error, t("failed")));
    }
  };
  const open = async (doc: SpaceDocumentResponse) => {
    const tab = window.open("", "_blank");
    try {
      const file = await fetchApiFile(doc.url);
      const blob = TEXT_TYPES.test(doc.mimeType ?? "") ? new Blob([file.blob], { type: "text/plain" }) : file.blob;
      if (tab) tab.location.href = URL.createObjectURL(blob);
    } catch (error) {
      tab?.close();
      toast.error(errorMessage(error, t("failed")));
    }
  };

  return (
    <SpaceSection title={t("title")} hint={t("hint")}>
      <QuerySection
        query={both}
        isEmpty={(all) => all.docs.length === 0 && all.chats.length === 0}
        empty={{ icon: <FileText size={18} />, hint: t("empty") }}
        loadingRows={2}
      >
        {({ docs: list, chats: sharedChats }) => (
          <ul>
            {sharedChats.map((chat) => (
              <li key={chat.uuid}>
                <ListItem
                  flat
                  onClick={() => navigate(`/chat/${chat.uuid}`)}
                  leading={<MessageCircle size={15} className="text-content-tertiary" />}
                  description={t("chatBy", { name: chat.userName ?? "", when: formatRelativeTime(chat.updatedAt) })}
                >
                  {chat.title || t("untitled")}
                </ListItem>
              </li>
            ))}
            {list.map((doc) => (
              <li key={doc.uuid}>
                <ListItem
                  flat
                  leading={<FileText size={15} className="text-content-tertiary" />}
                  description={[
                    doc.version ? t("version", { version: doc.version }) : null,
                    t("by", { name: doc.sharedByName ?? "", when: formatRelativeTime(doc.createdAt) }),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  trailing={
                    <span className="flex items-center gap-1">
                      <IconButton
                        icon={<ExternalLink />}
                        aria-label={t("open", { name: doc.filename })}
                        variant="ghost"
                        size="sm"
                        onClick={() => void open(doc)}
                      />
                      <IconButton
                        icon={<Download />}
                        aria-label={t("download", { name: doc.filename })}
                        variant="ghost"
                        size="sm"
                        onClick={() => void download(doc)}
                      />
                      {doc.canRemove && (
                        <IconButton
                          icon={<Trash2 />}
                          aria-label={t("remove", { name: doc.filename })}
                          variant="ghost"
                          size="sm"
                          onClick={() => remove.mutate({ path: { ...path, document_uuid: doc.uuid } })}
                        />
                      )}
                    </span>
                  }
                >
                  {doc.filename}
                </ListItem>
              </li>
            ))}
          </ul>
        )}
      </QuerySection>
    </SpaceSection>
  );
}
